///
/// EarthBrowser
///
/// Created by Matt Giger on 9/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Story Tours list for the Views menu. Rows play a story (reader-paced);
/// the pencil opens the editor. "New story" creates an empty draft and
/// opens the editor for capture-based authoring.
///

import { setStoryEditor } from "@/redux/dialogSlice"
import { sendCameraCommand } from "@/redux/earthBrowserSlice"
import { deleteStory, upsertStory, type Story } from "@/redux/storiesSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { useShareLink } from "@/hooks/useShareLink"
import { createStory } from "@/utils/stories"
import { relativeTime } from "@/utils/savedViews"
import { cn } from "@/utils/utils"
import { Check, Link, Pencil, Play, Plus, Search, Trash2 } from "lucide-react"
import { useCallback, useMemo, useState } from "react"

interface StoryRowProps {
	story: Story
	onPlay: (story: Story) => void
	onEdit: (storyId: string) => void
}

function StoryRow({ story, onPlay, onEdit }: StoryRowProps) {
	const dispatch = useAppDispatch()
	const { shareStory } = useShareLink()
	const signedIn = useAppSelector(state => !!state.user.userProfile)
	const [linkCopied, setLinkCopied] = useState(false)
	const [confirmingDelete, setConfirmingDelete] = useState(false)

	const stopLabel = story.stops.length === 1 ? "1 stop" : `${story.stops.length} stops`

	// Server-backed short URL (?s=) carrying the full story snapshot;
	// requires sign-in (a param URL can't represent the story)
	const handleCopyLink = (e: React.MouseEvent) => {
		e.stopPropagation()
		void shareStory(story).then(url => {
			if (!url) return
			return navigator.clipboard.writeText(url).then(() => {
				setLinkCopied(true)
				setTimeout(() => setLinkCopied(false), 1500)
			})
		})
	}

	// Two-step delete: first click arms (icon turns red), second deletes
	const handleDelete = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (!confirmingDelete) {
			setConfirmingDelete(true)
			return
		}
		dispatch(deleteStory(story.id))
	}

	const handleEdit = (e: React.MouseEvent) => {
		e.stopPropagation()
		onEdit(story.id)
	}

	// An empty draft has nothing to play; the row opens the editor instead
	const activate = () => {
		if (story.stops.length > 0) {
			onPlay(story)
		} else {
			onEdit(story.id)
		}
	}

	return (
		<div
			onClick={activate}
			onMouseLeave={() => setConfirmingDelete(false)}
			role="button"
			tabIndex={0}
			onKeyDown={e => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					activate()
				}
			}}
			className={cn(
				"group/row flex items-center gap-2.5 w-full px-2 py-1.5 rounded-lg text-left cursor-pointer",
				"hover:bg-white/10 active:bg-white/20 transition-colors duration-200",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
			)}
			title={story.stops.length > 0 ? `Play "${story.title}"` : `Edit "${story.title}"`}
		>
			<div className="w-8 h-8 rounded-md bg-slate-900/60 flex items-center justify-center flex-shrink-0">
				{story.stops.length > 0
					? <Play className="w-3.5 h-3.5 text-cyan-300" />
					: <Pencil className="w-3.5 h-3.5 text-white/40" />}
			</div>
			<div className="flex-1 min-w-0">
				<div className="text-sm text-white/85 truncate">{story.title}</div>
				<div className="text-[10px] text-white/40 truncate">{stopLabel} · {relativeTime(story.updatedAt)}</div>
			</div>
			<div className="flex items-center gap-0.5 flex-shrink-0">
				{signedIn && story.stops.length > 0 && (
					<button
						onClick={handleCopyLink}
						className={cn(
							"w-6 h-6 rounded-md flex items-center justify-center transition-colors duration-200",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
							linkCopied
								? "text-emerald-400"
								: "text-white/35 hover:text-white hover:bg-white/10"
						)}
						title="Copy link to this story"
						aria-label={`Copy link to ${story.title}`}
					>
						{linkCopied ? <Check className="w-3.5 h-3.5" /> : <Link className="w-3.5 h-3.5" />}
					</button>
				)}
				<button
					onClick={handleEdit}
					className={cn(
						"w-6 h-6 rounded-md flex items-center justify-center transition-colors duration-200",
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
						"text-white/35 hover:text-white hover:bg-white/10"
					)}
					title="Edit story"
					aria-label={`Edit ${story.title}`}
				>
					<Pencil className="w-3.5 h-3.5" />
				</button>
				<button
					onClick={handleDelete}
					className={cn(
						"w-6 h-6 rounded-md flex items-center justify-center transition-colors duration-200",
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
						confirmingDelete
							? "text-red-400 bg-red-500/20 hover:bg-red-500/30"
							: "text-white/35 hover:text-red-400 hover:bg-white/10"
					)}
					title={confirmingDelete ? "Click again to delete" : "Delete story"}
					aria-label={confirmingDelete ? `Confirm delete ${story.title}` : `Delete ${story.title}`}
				>
					<Trash2 className="w-3.5 h-3.5" />
				</button>
			</div>
		</div>
	)
}

interface StoriesListProps {
	/** Close the hosting menu after an action */
	onDone: () => void
}

/** Search + new story + rows */
export function StoriesList({ onDone }: StoriesListProps) {
	const dispatch = useAppDispatch()
	const stories = useAppSelector(state => state.stories.stories)
	const [search, setSearch] = useState("")

	const sorted = useMemo(() => {
		const query = search.trim().toLowerCase()
		return Object.values(stories)
			.filter(story =>
				!query ||
				story.title.toLowerCase().includes(query) ||
				story.tags.some(tag => tag.toLowerCase().includes(query)))
			.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
	}, [stories, search])

	const handlePlay = useCallback((story: Story) => {
		dispatch(sendCameraCommand({ command: "story/play", data: { storyId: story.id } }))
		onDone()
	}, [dispatch, onDone])

	const handleEdit = useCallback((storyId: string) => {
		dispatch(setStoryEditor(storyId))
		onDone()
	}, [dispatch, onDone])

	const handleNewStory = useCallback(() => {
		const story = createStory()
		dispatch(upsertStory(story))
		dispatch(setStoryEditor(story.id))
		onDone()
	}, [dispatch, onDone])

	const empty = Object.keys(stories).length === 0

	return (
		<div className="flex flex-col gap-1.5 min-h-0">
			<div className="relative flex-shrink-0">
				<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" />
				<input
					value={search}
					onChange={e => setSearch(e.target.value)}
					placeholder="Search stories…"
					className={cn(
						"w-full pl-8 pr-2 py-1.5 text-sm rounded-lg",
						"bg-white/5 text-white border border-white/10",
						"focus:outline-none focus:border-cyan-500/50 placeholder:text-white/40"
					)}
				/>
			</div>

			<button
				onClick={handleNewStory}
				className={cn(
					"flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs rounded-lg flex-shrink-0",
					"bg-cyan-500/15 text-cyan-300 border border-cyan-500/40",
					"hover:bg-cyan-500/25 transition-colors duration-200",
					"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
				)}
			>
				<Plus className="w-3.5 h-3.5" />
				New story
			</button>

			<div className="flex-1 min-h-0 overflow-y-auto">
				{sorted.length > 0 ? (
					sorted.map(story => (
						<StoryRow key={story.id} story={story} onPlay={handlePlay} onEdit={handleEdit} />
					))
				) : (
					<p className="px-2 py-3 text-xs text-white/40 text-center">
						{empty ? "No stories yet — create one and add stops as you explore" : "No stories match your search"}
					</p>
				)}
			</div>
		</div>
	)
}
